import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AgricolaExitConfirmDialogComponent } from '../steps/agricola-exit-confirm-dialog/agricola-exit-confirm-dialog.component';
import { agricolaEmailValidator, agricolaPositiveIntegerValidator } from '../../agricola-validators';
import { AgricolaLoadingState, AgricolaService } from '../../../../services/agricola.service';
import {
  ALL_STEP_GROUPS,
  WizardStepDefinition,
  WizardStepKey,
  resolveWizardSteps
} from '../agricola-wizard-steps.config';

@Component({
  selector: 'app-agricola-policy-wizard',
  templateUrl: './agricola-policy-wizard.component.html',
  styleUrls: ['./agricola-policy-wizard.component.css'],
  standalone: false
})
export class AgricolaPolicyWizardComponent implements OnInit, OnDestroy {

  wizardForm!: FormGroup;
  private wizardFormSubscription = new Subscription();
  private loadingStateSubscription = new Subscription();
  private holderPatchedAfterSearch = false;
  holderLoading = false;
  holderLoadedKey = 0;
  // True when the holder was prefilled from a Banco Agrícola client search/load.
  // Used to lock client-info fields in the assistance flow.
  holderFromBank = false;

  // Snapshot, taken at holder-load time, of the client-info form controls that
  // came back populated from Banco Agrícola. Only these get locked (readonly) in
  // the assistance flow; fields the bank returned empty stay editable. Captured
  // before syncCurrentInsuranceInfo() rebuilds the holder from the form, so
  // typing into an empty field never locks it afterwards.
  lockedClientFields: string[] = [];

  // Maps each lockable client-info control to the holder property that backs it
  // (see patchHolderData). A control is locked only when its holder value is
  // non-empty.
  private readonly clientInfoLockMap: Record<string, string> = {
    firstName: 'first_name',
    middleName: 'middle_name',
    firstLastName: 'last_name',
    secondLastName: 'last_name_2',
    marriedLastName: 'married_last_name',
    nit: 'nit',
    documentType: 'identification_type',
    documentNumber: 'identification_id',
    birthDate: 'birthday',
    gender: 'gender',
    country: 'country_id',
    nationality: 'nationality',
    address: 'address',
    phone: 'phone1',
    mobile: 'mobile'
  };

  // Once the apertura finishes (policy issued / sale emitted) leaving the wizard
  // is expected, so the exit-confirmation guard is skipped from that point on.
  policyIssued = false;

  activeSteps: WizardStepDefinition[] = resolveWizardSteps('', false);

  selectedIndex = 0;

  // Labels consumed by the step components' progress UI. In Telemarketing mode
  // the final step does not cobra/emite in place: it only records the sale as
  // PENDING and returns a process number, so its label reflects that instead of
  // "Pago y apertura de póliza".
  get steps(): string[] {
    const tmk = this.agricolaService.tmkMode;
    return this.activeSteps.map(step =>
      tmk && step.key === 'payment' ? 'Emisión de número de proceso' : step.label
    );
  }

  // Identity of the step at the current position; the template renders by key
  // so the same position can map to a different component per flow.
  get currentStepKey(): WizardStepKey | null {
    return this.activeSteps[this.selectedIndex]?.key ?? null;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private router: Router,
    private agricolaService: AgricolaService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.holderLoading = !!this.agricolaService.loadingSnapshot?.holder;
    if (this.hasLoadedHolderData()) {
      this.patchDefaultWizardData();
      this.patchHolderData();
      this.holderLoadedKey++;
      this.holderFromBank = true;
      this.computeLockedClientFields();
    } else {
      // Sin titular cargado (refresh, URL directa o búsqueda fallida) el wizard
      // arranca vacío: nunca se precargan datos de prueba que puedan emitirse.
      this.patchDefaultWizardData();
    }
    this.bindLoadingState();
    this.recomputeSteps();
    this.syncCurrentInsuranceInfo();
    this.wizardFormSubscription = this.wizardForm.valueChanges.subscribe(() => {
      this.syncCurrentInsuranceInfo();
    });
    // Whether the dependants step belongs to the flow comes from the selected
    // plan's config, which is only known once the premium step loads plans and
    // picks one. Do NOT distinctUntilChanged here: when the user reselects a
    // product they already visited, the premium step re-sets planOption to the
    // same value it held for that product, and a distinct filter would swallow
    // that emission — leaving recomputeSteps stale and the dependants step
    // missing. Recomputing on every set is cheap and keeps the step list correct.
    this.wizardFormSubscription.add(
      this.wizardForm.get('step3.premiumData.planOption')?.valueChanges
        .subscribe(() => this.recomputeSteps())
    );
  }

  ngOnDestroy(): void {
    this.wizardFormSubscription.unsubscribe();
    this.loadingStateSubscription.unsubscribe();
  }

  get step1Group(): FormGroup {
    return this.wizardForm.get('step1') as FormGroup;
  }

  get step2Group(): FormGroup {
    return this.wizardForm.get('step2') as FormGroup;
  }

  get step3Group(): FormGroup {
    return this.wizardForm.get('step3') as FormGroup;
  }

  get step4Group(): FormGroup {
    return this.wizardForm.get('step4') as FormGroup;
  }

  get step4bGroup(): FormGroup {
    return this.wizardForm.get('step4b') as FormGroup;
  }

  get step5Group(): FormGroup {
    return this.wizardForm.get('step5') as FormGroup;
  }

  get pepGroup(): FormGroup {
    return this.wizardForm.get('step1.pep') as FormGroup;
  }

  get selectedProductCode(): string {
    return String(this.wizardForm?.get('step1.productSelection.product')?.value || '');
  }

  get insuredIdentificationId(): string {
    return String(this.wizardForm?.get('step1.mainData.documentNumber')?.value || '');
  }

  get maxDependants(): number | null {
    // Delegates to the service so the cap uses the same plan->product resolution
    // and spelling tolerance as allowsDependants; reading only plan.config with a
    // single spelling here let a mismatched key drop the cap and pass extra
    // dependants (e.g. max=1 accepted 2).
    return this.agricolaService.resolveMaxDependants(this.wizardForm?.getRawValue());
  }

  buildForm() {
    this.wizardForm = this.fb.group({
      step1: this.fb.group({
        mainData: this.fb.group({
          firstName: ['', Validators.required],
          middleName: [''],
          firstLastName: ['', Validators.required],
          secondLastName: [''],
          marriedLastName: ['', Validators.required],
          nit: ['', Validators.required],
          documentType: ['', Validators.required],
          documentNumber: ['', Validators.required],
          code: [''],
          accountType: [''],
          civilStatus: ['', Validators.required],
          birthDate: ['', Validators.required],
          age: ['', Validators.required],
          gender: ['', Validators.required]
        }),
        complementary: this.fb.group({
          occupation: ['', Validators.required],
          salaryRange: ['', Validators.required],
          country: ['4', Validators.required],
          nationality: ['', Validators.required],
          address: ['', Validators.required],
          department: [''],
          municipality: [''],
          district: [''],
          destination: [''],
          creationAddress: [''],
          // Vigencia siempre arranca hoy; el vendedor puede adelantarla desde el
          // campo, pero nunca sale vacía hacia date_effective.
          effectiveDate: [this.todayIsoDate()],
          email: ['', [Validators.required, agricolaEmailValidator()]],
          phone: ['', Validators.required],
          phone2: [''],
          mobile: ['', [Validators.required, agricolaPositiveIntegerValidator('invalidMobile')]]
        }),
        productSelection: this.fb.group({
          ally: ['GURU'],
          product: ['Vida']
        }),
        pep: this.fb.group({
          isPep: ['no'],
          position: [''],
          startDate: [''],
          endDate: [''],
          pepNationality: [''],
          pepDocumentType: [''],
          pepDocumentNumber: [''],
          fundsOrigin: [''],
          incomeSource: [''],
          projectedIncome: [''],
          relativePep: ['no'],
          relativePosition: [''],
          relativeStartDate: [''],
          relativeEndDate: [''],
          relativeNationality: [''],
          relativeDocumentType: [''],
          relativeDocumentNumber: [''],
          relativeFundsOrigin: [''],
          relativeIncomeSource: [''],
          relativeProjectedIncome: ['']
        })
      }),
            step2: this.fb.group({
        questions: this.fb.group({
          tuberculosis: ['no'],
          hipertension: ['no'],
          mentales: ['no'],
          cardiovasculares: ['no'],
          cerebro: ['no'],
          renales: ['no'],
          pulmonares: ['no'],
          diabetes: ['no'],
          delirium: ['no'],
          epilepsia: ['no'],
          cancer: ['no'],
          leucemias: ['no'],
          cirrosis: ['no'],
          alcoholismo: ['no'],
          hepatitis: ['no'],
          ulcera: ['no'],
          vih: ['no'],
          discapacidad: ['no'],
          hospitalizaciones: ['no'],
          otrasEnfermedades: ['']
        }),
        confirmation: this.fb.group({
          accepted: [false]
        })
      }),
      step3: this.fb.group({
        premiumData: this.fb.group({
          ageRange: ['18-65'],
          planOption: ['', Validators.required],
          paymentMethod: ['Mensual', Validators.required],
          premiumResult: ['']
        })
      }),
      step4: this.fb.group({
        beneficiaries: this.fb.array([]),
        totalParticipation: [0]
      }),
      step4b: this.fb.group({
        dependants: this.fb.array([])
      }),
      step5: this.fb.group({
      paymentInfo: this.fb.group({
        // Vacíos hasta que /clients/accounts responda: si la consulta falla el
        // vendedor reintenta, pero nunca queda una cuenta ajena que valide.
        bankData: ['', Validators.required],
        accountType: ['', Validators.required],
        accountNumber: ['', Validators.required],
        accountName: ['', Validators.required],
        accountStatus: ['', Validators.required],
        autoDebitAuthorized: [false],
        acceptsMulticharge: ['0'],
        cardLast4: [''],
        paymentName: [''],
        expirationDate: [''],
        cardType: [''],
        cardAccountType: ['']
      }),
      sellerInfo: this.fb.group({
        sellerType: ['', Validators.required],
        sellerCode: ['', Validators.required],
        sellerName: ['', Validators.required],
        agencyCode: ['', Validators.required]
      })
    })
    });
  }

  private todayIsoDate(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private patchDefaultWizardData(): void {
    this.wizardForm.get('step1.productSelection')?.patchValue({
      ally: 'GURU',
      product: 'Vida'
    });

    this.wizardForm.get('step1.pep')?.patchValue({
      isPep: 'no',
      position: '',
      startDate: '',
      endDate: '',
      pepNationality: '',
      pepDocumentType: '',
      pepDocumentNumber: '',
      fundsOrigin: '',
      incomeSource: '',
      projectedIncome: '',
      relativePep: 'no',
      relativePosition: '',
      relativeStartDate: '',
      relativeEndDate: '',
      relativeNationality: '',
      relativeDocumentType: '',
      relativeDocumentNumber: '',
      relativeFundsOrigin: '',
      relativeIncomeSource: '',
      relativeProjectedIncome: ''
    });
  }

  private patchHolderData(): void {
    const holder = this.agricolaService.currentInsuranceInfo?.holder;

    if (!holder) {
      return;
    }

    this.patchExistingValues('step1.mainData', {
      firstName: holder.first_name,
      middleName: holder.middle_name,
      firstLastName: holder.last_name,
      secondLastName: holder.last_name_2,
      marriedLastName: holder.married_last_name,
      nit: holder.nit,
      documentType: holder.identification_type,
      documentNumber: holder.identification_id,
      code: holder.code,
      accountType: holder['account_type'],
      civilStatus: holder.civil_status,
      birthDate: holder.birthday,
      age: holder.age,
      gender: holder.gender
    }, false);

    this.patchExistingValues('step1.complementary', {
      occupation: holder.occupation,
      salaryRange: holder.salary_range,
      country: holder.country_id,
      nationality: holder.nationality,
      address: holder.address,
      email: holder.email,
      phone: holder.phone1,
      phone2: holder.phone2,
      mobile: holder.mobile
    }, true);

    this.wizardForm.get('step1.complementary')?.patchValue({
      department: '',
      municipality: '',
      district: ''
    }, { emitEvent: false });
  }

  // Captures which client-info controls Banco Agrícola returned populated, so
  // only those are locked downstream. Reads the freshly loaded holder before any
  // form-driven rebuild overwrites it.
  private computeLockedClientFields(): void {
    const holder = this.agricolaService.currentInsuranceInfo?.holder as Record<string, unknown> | undefined;

    if (!holder) {
      this.lockedClientFields = [];
      return;
    }

    this.lockedClientFields = Object.entries(this.clientInfoLockMap)
      .filter(([, holderProp]) => this.toDisplayValue(holder[holderProp]) !== '')
      .map(([field]) => field);
  }

  private patchExistingValues(path: string, values: Record<string, unknown>, emitEvent = false): void {
    const patch = Object.entries(values).reduce((acc, [key, value]) => {
      const normalizedValue = this.toDisplayValue(value);
      if (normalizedValue) {
        acc[key] = normalizedValue;
      }
      return acc;
    }, {} as Record<string, string>);

    this.wizardForm.get(path)?.patchValue(patch, { emitEvent });
  }

  private bindLoadingState(): void {
    if (!this.agricolaService.loadingState$) {
      return;
    }

    this.loadingStateSubscription = this.agricolaService.loadingState$.subscribe((state: AgricolaLoadingState) => {
      const wasLoading = this.holderLoading;
      this.holderLoading = state.holder;

      if (state.holder) {
        this.holderPatchedAfterSearch = false;
        return;
      }

      if (wasLoading && !this.holderPatchedAfterSearch && this.hasLoadedHolderData()) {
        this.patchHolderData();
        this.holderLoadedKey++;
        this.holderFromBank = true;
        this.computeLockedClientFields();
        this.holderPatchedAfterSearch = true;
        this.syncCurrentInsuranceInfo();
      }
    });
  }

  private hasLoadedHolderData(): boolean {
    const holder = this.agricolaService.currentInsuranceInfo?.holder;

    if (!holder) {
      return false;
    }

    return [
      holder.identification_id,
      holder.name,
      holder.first_name,
      holder.last_name,
      holder.email,
      holder.mobile
    ].some(value => !!this.toDisplayValue(value));
  }

  private toDisplayValue(value: unknown): string {
    return value == null ? '' : String(value).trim();
  }

  goBack(): void {
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
      return;
    }

    // Leaving from the first step exits the apertura; the route guard shows the
    // confirmation dialog before the navigation completes.
    this.router.navigate(['/agricola']);
  }

  goToStep(index: number): void {
    // Permitir avanzar o retroceder a cualquier step válido
    if (index >= 0 && index < this.activeSteps.length) {
      this.syncCurrentInsuranceInfo();
      this.selectedIndex = index;
    }
  }

  // Triggered when the holder step applies a product; rebuild the flow so the
  // steps shown match the selected product / assistance combination.
  onProductApplied(): void {
    this.recomputeSteps();
  }

  // Triggered when the premium step picks a different assistance product from its
  // boxes. Swap the selected product and re-validate so the premium step reloads
  // the new product's plans (its [productCode] input changes accordingly).
  onPremiumProductChange(productId: string): void {
    if (!productId) {
      return;
    }

    this.wizardForm.get('step1.productSelection.product')?.setValue(productId);
    this.agricolaService.clearAssistanceState();
    this.wizardFormSubscription.add(
      this.agricolaService.validateAssistanceProduct(productId).subscribe({
        next: () => {
          this.recomputeSteps();
          this.syncCurrentInsuranceInfo();
        }
      })
    );
  }

  private recomputeSteps(): void {
    const productCode = this.agricolaService.getInternalProductCode(this.selectedProductCode);
    this.activeSteps = resolveWizardSteps(productCode, this.agricolaService.isAssistanceProduct, {
      allowsDependants: this.agricolaService.allowsDependants(this.wizardForm?.getRawValue())
    });
    this.applyStepActivation();

    const lastIndex = this.activeSteps.length - 1;
    if (this.selectedIndex > lastIndex) {
      this.selectedIndex = lastIndex;
    }
  }

  // Enable only the form groups that belong to the active flow so excluded
  // steps' required validators and stale values never block or leak into the
  // emission. Disabled controls are still included by getRawValue(), so the
  // full captured payload remains complete.
  private applyStepActivation(): void {
    const activeGroups = new Set(this.activeSteps.map(step => step.group));

    ALL_STEP_GROUPS.forEach(groupName => {
      const control = this.wizardForm.get(groupName);
      if (!control) {
        return;
      }

      if (activeGroups.has(groupName)) {
        if (control.disabled) {
          control.enable({ emitEvent: false });
        }
      } else if (control.enabled) {
        control.disable({ emitEvent: false });
      }
    });
  }

  async submit(): Promise<void> {
    console.log(await this.agricolaService.emitSale(this.wizardForm.getRawValue()));
  }

  private syncCurrentInsuranceInfo(): void {
    this.agricolaService.updateCurrentInsuranceFromWizard(this.wizardForm.getRawValue());
  }

  cancel() {
    console.log('Cancelar wizard');
  }

  goHome(): void {
    this.router.navigate(['/agricola']);
  }

  // After the policy is issued, the stepper back arrow on the summary screen
  // returns to the client search instead of stepping back through the wizard.
  goToClientSearch(): void {
    this.router.navigate(['/agricola/client-search']);
  }

  // Marks the apertura as finished so the exit guard stops prompting once the
  // sale has been emitted / the policy number screen is shown.
  onPolicyIssued(): void {
    this.policyIssued = true;
  }

  // CanDeactivate hook: free navigation once the apertura is done, otherwise ask
  // the user to confirm losing the in-progress data.
  canDeactivate(): boolean | Observable<boolean> {
    if (this.policyIssued) {
      return true;
    }

    const dialogRef = this.dialog.open(AgricolaExitConfirmDialogComponent, {
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'sis-dialog-panel'
    });

    return dialogRef.afterClosed().pipe(map(confirmed => !!confirmed));
  }

}
